import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  appendLiveText,
  appendLiveThinking,
  applyLiveToolResult,
  createConversation,
  getConversationDetail,
  streamConsoleMessage,
  toolCallViewFromUse,
  type AssistantBlock,
  type ConsoleEvent,
  type UsageEvent,
} from '../../services/agentConsole';
import { AgentFileCard } from '../agentConsole/AgentFileCard';
import {
  liftAgentFileCards,
  mergeAgentFiles,
} from '../agentConsole/agentFileCards';
import { AssistantTurn, UsageMeta } from '../agentConsole/AssistantTurnView';
import {
  chatMsgsFromDetail,
  clearModelerConvId,
  loadModelerConvId,
  saveModelerConvId,
  type ChatMsg,
} from './modelerSession';

// Embedded CPLEX Modeler chat for the Sankey screen. Talks to the
// `cplex_modeler` Agent Console profile so you can give a constraint in plain
// language and watch it open the current version, edit the model, re-solve, and
// submit a new version — which then shows up in the Version switcher.
//
// The transcript is rendered with the SAME components as the Agent Console page
// (`AssistantTurnView`): assistant markdown, the extended-thinking trace, and
// the collapsible tool-call traces (name · status · input · result), plus a
// per-turn token/cost line. Keeping one renderer means the two read identically.

interface Props {
  /** Called when a turn finishes — refresh the version list (a version may be new). */
  onTurnComplete?: () => void;
  onClose?: () => void;
}

const PANEL: CSSProperties = {
  position: 'fixed',
  top: 44,
  right: 0,
  width: 420,
  maxWidth: '100vw',
  height: 'calc(100vh - 44px)',
  background: '#f8fafc',
  color: 'var(--cm-gray-800)',
  borderLeft: '1px solid #e2e8f0',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
  boxShadow: '-4px 0 16px rgba(0,0,0,0.12)',
};

const USER_BUBBLE: CSSProperties = {
  alignSelf: 'flex-end',
  maxWidth: '92%',
  background: '#1d4ed8',
  color: 'white',
  borderRadius: '1rem 1rem 0.25rem 1rem',
  padding: '8px 12px',
  fontSize: 14,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const ASSISTANT_BUBBLE: CSSProperties = {
  alignSelf: 'flex-start',
  maxWidth: '92%',
  display: 'flex',
  flexDirection: 'column',
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '1rem 1rem 1rem 0.25rem',
  padding: '10px 12px',
};

export function CplexModelerChatPanel({ onTurnComplete, onClose }: Props) {
  const [convId, setConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  // True until the mount-time rehydrate settles — gates the loading line (vs the
  // empty-state hint) and blocks a send from racing the fetch.
  const [rehydrating, setRehydrating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const toolSeq = useRef(0);

  // On mount, rehydrate from the backend (the source of truth): read the
  // lightweight convId pointer from localStorage and GET the conversation's
  // turns. The transcript is NEVER restored from localStorage — only the
  // pointer is, so a cleared cache or a second browser can't blank or fork the
  // thread. A stale pointer (conversation deleted / fetch failed) is dropped so
  // the panel starts fresh instead of wedging on a dead id.
  useEffect(() => {
    const pointer = loadModelerConvId();
    if (!pointer) {
      setRehydrating(false);
      return;
    }
    let cancelled = false;
    setConvId(pointer);
    getConversationDetail(pointer)
      .then((detail) => {
        if (!cancelled) setMessages(chatMsgsFromDetail(detail));
      })
      .catch(() => {
        if (cancelled) return;
        clearModelerConvId();
        setConvId(null);
        setMessages([]);
      })
      .finally(() => {
        if (!cancelled) setRehydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist ONLY the conversation id (a pointer). A reopen/reload re-fetches the
  // same thread from the backend; no transcript ever touches localStorage.
  useEffect(() => {
    if (convId) saveModelerConvId(convId);
  }, [convId]);

  function resetSession() {
    abortRef.current?.abort();
    clearModelerConvId();
    setConvId(null);
    setMessages([]);
    setError(null);
    setStreaming(false);
  }

  function updateLiveAssistant(mut: (blocks: AssistantBlock[]) => AssistantBlock[], usage?: UsageEvent) {
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (!last || last.role !== 'assistant') return prev;
      next[next.length - 1] = {
        ...last,
        blocks: usage ? last.blocks : mut(last.blocks),
        usage: usage ?? last.usage,
      };
      return next;
    });
  }

  async function send() {
    const prompt = input.trim();
    if (!prompt || streaming || rehydrating) return;
    let id = convId;
    setInput('');
    setError(null);
    setMessages((m) => [
      ...m,
      { role: 'user', text: prompt },
      { role: 'assistant', blocks: [], usage: null },
    ]);
    setStreaming(true);
    try {
      if (!id) {
        const convo = await createConversation({ title: 'CPLEX Modeler (Sankey)' });
        id = convo.id;
        setConvId(id);
      }
      const controller = new AbortController();
      abortRef.current = controller;
      await streamConsoleMessage(
        id,
        prompt,
        (evt: ConsoleEvent) => {
          switch (evt.type) {
            case 'text':
              updateLiveAssistant((bs) => appendLiveText(bs, evt.text));
              break;
            case 'thinking':
              updateLiveAssistant((bs) => appendLiveThinking(bs, evt.text));
              break;
            case 'tool_use':
              updateLiveAssistant((bs) => [
                ...bs,
                { kind: 'tool', tool: toolCallViewFromUse(evt, `live-${toolSeq.current++}`) },
              ]);
              break;
            case 'tool_result':
              // A successful attach_file frame becomes a file card the moment
              // its ack lands (pending/errored chips stay in the trace).
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (!last || last.role !== 'assistant') return prev;
                const applied = applyLiveToolResult(
                  last.blocks,
                  evt.tool_use_id,
                  evt.is_error,
                  evt.summary,
                );
                const { blocks: kept, files } = liftAgentFileCards(applied);
                next[next.length - 1] = {
                  ...last,
                  blocks: kept,
                  agentFiles: files.length
                    ? mergeAgentFiles(last.agentFiles, files)
                    : last.agentFiles,
                };
                return next;
              });
              break;
            case 'result':
              updateLiveAssistant((bs) => bs, evt);
              break;
            case 'error':
              setError(evt.message);
              break;
            default:
              break;
          }
        },
        controller.signal,
        'cplex_modeler',
      );
      onTurnComplete?.();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? 'Turn failed.');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  return (
    <div style={PANEL}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid #e2e8f0',
          background: 'white',
        }}
      >
        <strong style={{ fontSize: 14 }}>CPLEX Modeler</strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {convId || messages.length > 0 ? (
            <button
              type="button"
              onClick={resetSession}
              title="Start a fresh conversation"
              style={{
                background: 'none',
                border: '1px solid #e2e8f0',
                color: '#475569',
                borderRadius: 6,
                padding: '2px 8px',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              ↻ Reset
            </button>
          ) : null}
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                fontSize: 18,
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {rehydrating ? (
          <p style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>
            Loading conversation…
          </p>
        ) : messages.length === 0 ? (
          <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            Give the agent a constraint or assumption and ask it to submit a new
            version — e.g. <em>“Block Safe-Cure 250 production on Thursdays, then
            submit it as a new version.”</em> It opens the current version, edits
            the model, re-solves, and submits; the new version then appears in the{' '}
            <strong>Version</strong> dropdown so you can render and A/B-compare it.
          </p>
        ) : null}
        {messages.map((m, i) => {
          if (m.role === 'user') {
            return (
              <div key={i} style={USER_BUBBLE}>
                {m.text}
              </div>
            );
          }
          const isLast = i === messages.length - 1;
          const isLive = streaming && isLast;
          return (
            <div key={i} style={ASSISTANT_BUBBLE}>
              {m.blocks.length > 0 ? (
                <AssistantTurn blocks={m.blocks} isStreaming={isLive} />
              ) : (
                <span style={{ color: '#64748b', fontStyle: 'italic', fontSize: 14 }}>Thinking…</span>
              )}
              {/* Files the agent attached this turn (attach_file). */}
              {m.agentFiles?.map((file) => (
                <AgentFileCard
                  key={file.attachmentId}
                  base="/admin/agent-console"
                  file={file}
                  conversationId={convId}
                />
              ))}
              {m.usage ? <UsageMeta usage={m.usage} /> : null}
            </div>
          );
        })}
      </div>

      {error ? (
        <div style={{ color: '#b91c1c', fontSize: 12, padding: '0 12px 8px' }}>{error}</div>
      ) : null}

      <div style={{ display: 'flex', gap: 6, padding: 10, borderTop: '1px solid #e2e8f0', background: 'white' }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Give the modeler a constraint…"
          disabled={streaming || rehydrating}
          rows={2}
          style={{
            flex: 1,
            resize: 'none',
            background: 'white',
            color: 'var(--cm-gray-800)',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            padding: 8,
            fontSize: 13,
          }}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={streaming || rehydrating || !input.trim()}
          style={{
            alignSelf: 'flex-end',
            padding: '8px 14px',
            borderRadius: 6,
            border: 'none',
            background: streaming || rehydrating || !input.trim() ? '#94a3b8' : '#1d4ed8',
            color: 'white',
            cursor: streaming || rehydrating ? 'default' : 'pointer',
          }}
        >
          {streaming ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
